import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  input,
  output,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
  inject,
  DestroyRef
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  ActivityStreamChangedProperty,
  ActivityStreamHelper,
  ActivityStreamItem
} from '@coin/shared/activity-stream/util';
import { EmployeeIdPipe } from '@coin/shared/auth/feature';
import { AuthComponentsModule } from '@coin/shared/auth/feature';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import { EnumToTextPipe, FormatUnknownValuePipe } from '@coin/shared/core/util-pipes';
import { TranslateModule } from '@ngx-translate/core';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'coin-c-activity-stream-multiple-items',
  templateUrl: './activity-stream-multiple-items.component.html',
  styleUrls: ['./activity-stream-multiple-items.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LegacyComponentsModule,
    TranslateModule,
    NgTemplateOutlet,
    MatIcon,
    FormatUnknownValuePipe,
    EnumToTextPipe,
    AuthComponentsModule,
    EmployeeIdPipe,
    AsyncPipe,
    V2ButtonComponent
  ]
})
export class ActivityStreamMultipleItemsComponent {
  private destroyRef = inject(DestroyRef);
  private events = inject(GlobalEventsService);
  private overlay = viewChild<ElementRef<HTMLElement>>('overlay');
  public changedProperties = input.required<ActivityStreamChangedProperty[]>();
  public item = input.required<ActivityStreamItem>();
  public showDetails = input<boolean>();
  public showDetailsClicked = output<void>();
  public closeDetails = output<void>();

  constructor() {
    this.events
      .listen('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.handleOutsideClick(event as MouseEvent);
      });
  }

  protected filteredItems = computed(() =>
    this.changedProperties().filter(changedProperty => !this.isPropertyHidden(changedProperty))
  );

  public isDisplayedUnformatted(property: ActivityStreamChangedProperty): boolean {
    return ActivityStreamHelper.isDisplayedUnformatted(property);
  }

  public isEmployeeChange(property: ActivityStreamChangedProperty): boolean {
    return ActivityStreamHelper.isEmployeeChangeProperty(property?.propertyName);
  }

  public nameEmployeeProperty(name: string): string {
    const employeeIdPostfix = 'EmployeeId';
    return name && name.indexOf(employeeIdPostfix) > 0 ? name.substring(0, name.indexOf(employeeIdPostfix)) : name;
  }

  private isPropertyHidden(property: ActivityStreamChangedProperty): boolean {
    return property.propertyName?.endsWith('CreatedById') || property.propertyName?.endsWith('UpdatedById');
  }

  public onShowClicked(event: MouseEvent): void {
    event.stopPropagation();
    this.showDetailsClicked.emit();
  }

  public onOverlayClicked(event: MouseEvent): void {
    event.stopPropagation();
  }

  public handleOutsideClick(event: MouseEvent): void {
    if (!this.showDetails()) return;
    const { target } = event;

    if (!(target instanceof Node)) return;
    const overlay = this.overlay()?.nativeElement;
    const clickedInsideOverlay = overlay?.contains(target);

    if (!clickedInsideOverlay) {
      this.closeDetails.emit();
    }
  }
}
